import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Check,
  X,
  Zap,
  Crown,
  Flame,
  Star,
  ArrowLeft,
  Loader2,
  Dumbbell,
  Users,
  Calendar
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

const onlinePlans = [
  {
    id: "basic",
    name: "Basic",
    price: "$9.99",
    period: "/month",
    trialPrice: "$1 to start",
    description: "Online workouts for self-starters",
    icon: Flame,
    features: [
      { text: "Full 60-Day Shred Program", included: true },
      { text: "Weekly workout videos", included: true },
      { text: "Warm-ups & stretching", included: true },
      { text: "Nutrition basics", included: true },
      { text: "Habit tracker", included: true },
      { text: "Boxing fundamentals", included: true },
      { text: "AI coaching", included: false },
      { text: "Direct access to Coach", included: false }
    ],
    popular: false
  },
  {
    id: "pro",
    name: "Pro",
    price: "$49.99",
    period: "/month",
    trialPrice: "$1 to start",
    description: "For serious fighters",
    icon: Star,
    features: [
      { text: "Everything in Basic PLUS:", included: true },
      { text: "Advanced boxing combos", included: true },
      { text: "Shadow boxing routines", included: true },
      { text: "AI coaching messages", included: true },
      { text: "Beast Mode workouts", included: true },
      { text: "Private accountability group", included: true },
      { text: "Monthly challenges", included: true },
      { text: "Progress tracking", included: true }
    ],
    popular: true
  },
  {
    id: "elite",
    name: "Elite",
    price: "$399",
    period: "/month",
    trialPrice: "$1 to start",
    description: "Complete transformation",
    icon: Crown,
    features: [
      { text: "Everything in Pro PLUS:", included: true },
      { text: "Weekly 1-on-1 coaching (Zoom)", included: true },
      { text: "Custom workout plans", included: true },
      { text: "Custom nutrition plan", included: true },
      { text: "Partner workouts", included: true },
      { text: "Daily accountability", included: true },
      { text: "Priority support", included: true },
      { text: "Private messaging with Coach", included: true }
    ],
    popular: false
  }
];

const inhousePlans = [
  {
    id: "dropin",
    name: "Drop-In",
    price: "$10",
    period: "/class",
    description: "Try a single class",
    icon: Zap,
    features: [
      { text: "Any single class", included: true },
      { text: "Boxing conditioning", included: true },
      { text: "All equipment provided", included: true },
      { text: "No commitment", included: true }
    ],
    popular: false,
    color: "text-yellow-500"
  },
  {
    id: "inhouse_3day",
    name: "3-Day Weekly",
    price: "$65",
    period: "/month",
    description: "Mon/Wed/Fri classes",
    icon: Calendar,
    features: [
      { text: "3 classes per week", included: true },
      { text: "Boxing & conditioning", included: true },
      { text: "Shadow boxing drills", included: true },
      { text: "Heavy bag work", included: true },
      { text: "Group motivation", included: true },
      { text: "Gloves & wraps included", included: true }
    ],
    popular: false,
    color: "text-primary"
  },
  {
    id: "inhouse_weekend",
    name: "Weekend Warrior",
    price: "$70",
    period: "/month",
    description: "Weekend burnout + boxing",
    icon: Dumbbell,
    features: [
      { text: "Saturday burnout sessions", included: true },
      { text: "Sunday boxing conditioning", included: true },
      { text: "Intense cardio boxing", included: true },
      { text: "Sparring basics", included: true },
      { text: "Pad work with trainers", included: true },
      { text: "Recovery stretching", included: true }
    ],
    popular: false,
    color: "text-orange-500"
  },
  {
    id: "coaching",
    name: "Coach Coop",
    price: "$120",
    period: "/month",
    description: "Boot camp + coaching combo",
    icon: Crown,
    features: [
      { text: "1-on-1 support", included: true },
      { text: "Form checks", included: true },
      { text: "Faster results", included: true },
      { text: "Unlimited boot camp", included: true },
      { text: "Custom workout plan", included: true },
      { text: "Nutrition plan", included: true },
      { text: "Weekly check-ins", included: true },
      { text: "EVERYTHING INCLUDED", included: true }
    ],
    popular: true,
    color: "text-accent"
  }
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [activeTab, setActiveTab] = useState("online");

  const handleSelectPlan = async (planId) => {
    if (!user) {
      toast.error("Please sign up first");
      navigate("/signup");
      return;
    }

    setLoading(planId);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Failed to start checkout");
    } finally {
      setLoading(null);
    }
  };

  const currentPlans = activeTab === "online" ? onlinePlans : inhousePlans;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">PRICING</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-4">
            TRAIN LIKE A <span className="text-primary">FIGHTER</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Online programs or in-house boxing classes. Choose your path.
          </p>
        </motion.div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-8">
          <div className="bg-secondary rounded-lg p-1 flex gap-1">
            <Button
              variant={activeTab === "online" ? "default" : "ghost"}
              onClick={() => setActiveTab("online")}
              className={`uppercase font-bold tracking-wider ${activeTab === "online" ? "bg-primary" : ""}`}
            >
              <Flame className="h-4 w-4 mr-2" />
              Online Programs
            </Button>
            <Button
              variant={activeTab === "inhouse" ? "default" : "ghost"}
              onClick={() => setActiveTab("inhouse")}
              className={`uppercase font-bold tracking-wider ${activeTab === "inhouse" ? "bg-primary" : ""}`}
            >
              <Users className="h-4 w-4 mr-2" />
              In-House Classes
            </Button>
          </div>
        </div>

        {/* Boxing Badge */}
        {activeTab === "inhouse" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/30 rounded-full mb-4">
              <span className="text-2xl">🥊</span>
              <span className="text-primary font-bold uppercase tracking-wider">Boxing & Conditioning Classes</span>
              <span className="text-2xl">🥊</span>
            </div>
            <div>
              <Link to="/schedule">
                <Button variant="outline" size="sm" className="border-primary/50 text-primary hover:bg-primary/10">
                  <Calendar className="h-4 w-4 mr-2" />
                  View Class Schedule
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {/* Pricing Grid */}
        <div className={`grid gap-6 ${currentPlans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
          {currentPlans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className={`h-full relative ${plan.popular ? 'border-primary glow-primary' : 'border-border'} bg-card`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider rounded-sm">
                    {activeTab === "inhouse" ? "Best Value" : "Most Popular"}
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <plan.icon className={`h-10 w-10 mx-auto mb-2 ${plan.color || (plan.popular ? 'text-primary' : 'text-muted-foreground')}`} />
                  <CardTitle className="uppercase tracking-tighter">{plan.name}</CardTitle>
                  <div className="mt-2">
                    <span className="text-4xl font-black">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                  {plan.trialPrice && (
                    <div className="text-sm text-accent font-bold mt-1">{plan.trialPrice}</div>
                  )}
                  <p className="text-sm text-muted-foreground mt-2">{plan.description}</p>
                </CardHeader>
                
                <CardContent className="flex flex-col h-full">
                  <ul className="space-y-3 mb-6 flex-grow">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2">
                        {feature.included ? (
                          <Check className="h-5 w-5 text-accent flex-shrink-0" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground/50 flex-shrink-0" />
                        )}
                        <span className={feature.included ? 'text-foreground' : 'text-muted-foreground/50'}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button
                    data-testid={`pricing-${plan.id}-btn`}
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={loading === plan.id || user?.subscription_tier === plan.id}
                    className={`w-full uppercase font-bold tracking-widest rounded-sm ${
                      plan.popular 
                        ? 'bg-primary hover:bg-primary/90' 
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    {loading === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : user?.subscription_tier === plan.id ? (
                      "Current Plan"
                    ) : activeTab === "inhouse" ? (
                      plan.id === "dropin" ? "Book Class" : "Get Started"
                    ) : (
                      "Get Started"
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12"
        >
          {activeTab === "online" ? (
            <p className="text-muted-foreground">
              All online plans include a 7-day trial for just $1. Cancel anytime.
            </p>
          ) : (
            <p className="text-muted-foreground">
              In-house classes at our gym. Contact us for schedule and location.
            </p>
          )}
        </motion.div>
      </main>
    </div>
  );
}
